import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import {
  CategoryDto,
  CreateIdeaRequest,
  IdeaCommentDto,
  IdeaDeliverableAttachmentDto,
  IdeaDto,
  IdeaListItemDto,
  IdeaTimelineItemDto,
  PagedResult
} from './ideas-api.types';

export type UpsertIdeaScoreRequest = {
  criterion1Score: number;
  criterion2Score: number;
  criterion3Score: number;
  criterion4Score: number;
  criterion5Score: number;
  notes?: string | null;
};

export type IdeaScoreDto = {
  reviewerId: string;
  criterion1Score: number;
  criterion2Score: number;
  criterion3Score: number;
  criterion4Score: number;
  criterion5Score: number;
  totalScore: number;
  notes?: string | null;
  createdAtUtc: string;
};

export type IdeaScoreSummaryDto = {
  currentUserScore: IdeaScoreDto | null;
  totalScore: number | null;
};

export type GetIdeasOptions = {
  search?: string;
  categoryId?: string;
  challengeId?: string;
  status?: string;
  assignedTo?: string;
  /** Impact scope: my-team | my-department | company-wide | customers | product */
  scope?: string;
  /** Effort band: low | medium | high (also accepts small/large on the API) */
  effort?: string;
  /** Owner department filter (canonical name, e.g. from department catalog) */
  department?: string;
  /** newest | trending */
  sort?: string;
};

@Injectable({ providedIn: 'root' })
export class IdeasApiService {
  constructor(private readonly http: HttpClient) {}

  getCategories(): Observable<CategoryDto[]> {
    return this.http.get<CategoryDto[]>('/api/categories');
  }

  getIdeas(page = 1, pageSize = 20): Observable<PagedResult<IdeaListItemDto>> {
    return this.getIdeasWithOptions({ page, pageSize });
  }

  getMyIdeas(page = 1, pageSize = 20, status?: string): Observable<PagedResult<IdeaListItemDto>> {
    const params: Record<string, string> = {
      page: String(page),
      pageSize: String(pageSize)
    };
    const normalizedStatus = (status ?? '').trim();
    if (normalizedStatus) {
      params['status'] = normalizedStatus;
    }
    return this.http.get<PagedResult<IdeaListItemDto>>('/api/ideas/mine', { params });
  }

  getFollowedIdeas(page = 1, pageSize = 20): Observable<PagedResult<IdeaListItemDto>> {
    return this.http.get<PagedResult<IdeaListItemDto>>('/api/ideas/following', {
      params: { page: String(page), pageSize: String(pageSize) }
    });
  }

  getIdeasWithOptions(options: { page?: number; pageSize?: number } & GetIdeasOptions): Observable<PagedResult<IdeaListItemDto>> {
    const page = options.page ?? 1;
    const pageSize = options.pageSize ?? 20;

    const params: Record<string, string> = {
      page: String(page),
      pageSize: String(pageSize)
    };

    const search = (options.search ?? '').trim();
    const categoryId = (options.categoryId ?? '').trim();
    const challengeId = (options.challengeId ?? '').trim();
    const status = (options.status ?? '').trim();
    const assignedTo = (options.assignedTo ?? '').trim();
    const scope = (options.scope ?? '').trim();
    const effort = (options.effort ?? '').trim();
    const department = (options.department ?? '').trim();
    const sort = (options.sort ?? '').trim();

    if (search) params['search'] = search;
    if (categoryId) params['categoryId'] = categoryId;
    if (challengeId) params['challengeId'] = challengeId;
    if (status) params['status'] = status;
    if (assignedTo) params['assignedTo'] = assignedTo;
    if (scope) params['scope'] = scope;
    if (effort) params['effort'] = effort;
    if (department) params['department'] = department;
    if (sort) params['sort'] = sort;

    return this.http.get<PagedResult<IdeaListItemDto>>('/api/ideas', { params });
  }

  createIdea(request: CreateIdeaRequest): Observable<IdeaDto> {
    return this.http.post<IdeaDto>('/api/ideas', request);
  }

  updateDraftIdea(id: string, request: CreateIdeaRequest): Observable<IdeaDto> {
    return this.http.put<IdeaDto>(`/api/ideas/${encodeURIComponent(id)}/draft`, request);
  }

  getIdeaById(id: string): Observable<IdeaDto> {
    return this.http.get<IdeaDto>(`/api/ideas/${encodeURIComponent(id)}`);
  }

  getIdeaTimeline(id: string): Observable<IdeaTimelineItemDto[]> {
    return this.http.get<IdeaTimelineItemDto[]>(`/api/ideas/${encodeURIComponent(id)}/timeline`);
  }

  voteIdea(id: string): Observable<void> {
    return this.http.post<void>(`/api/ideas/${encodeURIComponent(id)}/vote`, {});
  }

  unvoteIdea(id: string): Observable<void> {
    return this.http.delete<void>(`/api/ideas/${encodeURIComponent(id)}/vote`);
  }

  downvoteIdea(id: string): Observable<void> {
    return this.http.post<void>(`/api/ideas/${encodeURIComponent(id)}/downvote`, {});
  }

  undownvoteIdea(id: string): Observable<void> {
    return this.http.delete<void>(`/api/ideas/${encodeURIComponent(id)}/downvote`);
  }

  followIdea(id: string): Observable<void> {
    return this.http.post<void>(`/api/ideas/${encodeURIComponent(id)}/follow`, {});
  }

  unfollowIdea(id: string): Observable<void> {
    return this.http.delete<void>(`/api/ideas/${encodeURIComponent(id)}/follow`);
  }

  helpIdea(id: string): Observable<void> {
    return this.http.post<void>(`/api/ideas/${encodeURIComponent(id)}/help`, {});
  }

  unhelpIdea(id: string): Observable<void> {
    return this.http.delete<void>(`/api/ideas/${encodeURIComponent(id)}/help`);
  }

  getComments(ideaId: string, page = 1, pageSize = 50): Observable<IdeaCommentDto[]> {
    return this.http.get<IdeaCommentDto[]>(`/api/ideas/${encodeURIComponent(ideaId)}/comments`, {
      params: { page, pageSize }
    });
  }

  postComment(ideaId: string, body: string, parentCommentId?: string | null): Observable<IdeaCommentDto> {
    const payload: { body: string; parentCommentId?: string | null } = { body };
    const parent = (parentCommentId ?? '').trim();
    if (parent) {
      payload.parentCommentId = parent;
    }
    return this.http.post<IdeaCommentDto>(`/api/ideas/${encodeURIComponent(ideaId)}/comments`, payload);
  }

  updateComment(ideaId: string, commentId: string, body: string): Observable<IdeaCommentDto> {
    return this.http.put<IdeaCommentDto>(`/api/ideas/${encodeURIComponent(ideaId)}/comments/${encodeURIComponent(commentId)}`, {
      body
    });
  }

  deleteComment(ideaId: string, commentId: string): Observable<void> {
    return this.http.delete<void>(`/api/ideas/${encodeURIComponent(ideaId)}/comments/${encodeURIComponent(commentId)}`);
  }

  changeIdeaStatus(ideaId: string, body: { status: string; note?: string | null }): Observable<void> {
    return this.http.post<void>(`/api/ideas/${encodeURIComponent(ideaId)}/status`, body);
  }

  getIdeaScore(ideaId: string): Observable<IdeaScoreSummaryDto> {
    return this.http.get<IdeaScoreSummaryDto>(`/api/ideas/${encodeURIComponent(ideaId)}/score`);
  }

  upsertIdeaScore(ideaId: string, body: UpsertIdeaScoreRequest): Observable<unknown> {
    return this.http.post<unknown>(`/api/ideas/${encodeURIComponent(ideaId)}/score`, body);
  }

  setIdeaAssignee(ideaId: string, assignedReviewerId: string | null): Observable<void> {
    return this.http.put<void>(`/api/ideas/${encodeURIComponent(ideaId)}/assignee`, {
      assignedReviewerId
    });
  }

  uploadIdeaDeliverable(ideaId: string, file: File): Observable<IdeaDeliverableAttachmentDto> {
    return this.uploadIdeaDeliverables(ideaId, [file]).pipe(
      map((items) => items[0])
    );
  }

  /**
   * Multipart upload: field name `files` must match the API (`IFormFile` parts named "files").
   * Bytes are stored on `IdeaDeliverableAttachment.FileData` (no disk path).
   */
  uploadIdeaDeliverables(ideaId: string, files: File[]): Observable<IdeaDeliverableAttachmentDto[]> {
    const body = new FormData();
    for (const file of files) {
      body.append('files', file, file.name);
    }
    return this.http.post<IdeaDeliverableAttachmentDto[]>(`/api/ideas/${encodeURIComponent(ideaId)}/deliverables`, body);
  }

  downloadIdeaDeliverable(ideaId: string, attachmentId: string): Observable<Blob> {
    return this.http.get(`/api/ideas/${encodeURIComponent(ideaId)}/deliverables/${encodeURIComponent(attachmentId)}/file`, {
      responseType: 'blob'
    });
  }
}
